/** Key of the feature flag that turns the mocks on (registered in `featureFlags.ts`). */
export const DEV_MOCKS_FLAG = 'referentialMocks'

/**
 * Cold load of the mocks chunk (MSW, fixtures, catalogue) on DEV, possibly behind a VPN: a short
 * delay would give up on a start that was only slow.
 */
export const DEV_MOCKS_START_TIMEOUT_MS = 10_000

export function shouldEnableDevMocks({
  testingTooling,
  flags,
}: {
  testingTooling: boolean
  flags: ReadonlySet<string>
}) {
  return testingTooling && flags.has(DEV_MOCKS_FLAG)
}

type BootDevMocksOptions = {
  testingTooling: boolean
  flags: ReadonlySet<string>
  /**
   * Starts the mocks (dynamic import of `enableDevMocks`): never run unless they are wanted. It
   * receives a signal that is aborted when `bootDevMocks` gives up: a start that finishes late MUST
   * then do nothing (no worker, no badge), or it would switch the mocks on in the middle of a
   * session whose `/userinfo` was loaded without their permissions.
   */
  load: (signal: AbortSignal) => Promise<void>
  timeoutMs: number
  onFailure: (reason: unknown) => void
}

/**
 * Starts the mocks before the first render, only when wanted. Never rejects and never waits longer
 * than `timeoutMs`: a worker that fails or never registers (HTTP, private window) must not block or
 * blank the page: the failure is reported, the pending start is aborted and the app renders without
 * mocks.
 */
export async function bootDevMocks({
  testingTooling,
  flags,
  load,
  timeoutMs,
  onFailure,
}: BootDevMocksOptions): Promise<void> {
  if (!shouldEnableDevMocks({ testingTooling, flags })) return

  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<void>(resolve => {
    timer = setTimeout(() => {
      controller.abort()
      onFailure(new Error(`Dev mocks did not start within ${timeoutMs} ms`))
      resolve()
    }, timeoutMs)
  })

  try {
    await Promise.race([load(controller.signal), timeout])
  } catch (error) {
    onFailure(error)
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Renders the app, after the DEV mocks when they are wanted. This is what `index.tsx` calls: the
 * promise lives here, not in a top-level statement of the entry point.
 *
 * When the mocks are not wanted (UAT, PROD, DEV without the flag) `render` runs at once, in the
 * same tick, exactly as before the mocks existed: nothing is awaited. Otherwise it runs once the
 * mocks started, failed or timed out: `bootDevMocks` never rejects, so the app always renders.
 */
export function renderWhenDevMocksReady({
  render,
  ...options
}: BootDevMocksOptions & { render: () => void }): void {
  if (!shouldEnableDevMocks(options)) {
    render()
    return
  }

  void bootDevMocks(options).finally(render)
}
